// Header — versión 2.4.0:
//  · Removido el botón superior de "Por defecto · 14/14" (módulos).
//  · Búsqueda global activa: ejecuta /api/search?q= y muestra dropdown con
//    resultados (estudiantes, docentes, cuestionarios) clicables.
//  · Notificaciones activas: GET /api/notifications + popover con la lista,
//    botón "Marcar todo como leído", contador visible en la campana.
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, Search, X, User, BookOpenCheck, Briefcase, CheckCheck, Loader2, ArrowLeft, Home, LogOut, Building2, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { OWL_CLASSIC } from "@/lib/avatars";
import TourLauncher from "@/components/TourLauncher";
import Logo from "@/components/Logo";
import { ROLE_HOME } from "@/lib/navConfig";

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [tourOpen, setTourOpen] = useState(false);
  const [tourAuto, setTourAuto] = useState(false); // abierto solo por el auto-inicio → se auto-cierra
  const homePath = ROLE_HOME[user?.role] || "/";
  const atHome = location.pathname === homePath;
  const goBack = () => {
    // Si no hay historial dentro de la app, «Atrás» lleva al Inicio (nunca nos quedamos encerrados).
    if ((window.history.state?.idx ?? 0) > 0) nav(-1); else nav(homePath);
  };

  // Lanza el menú de tutoriales automáticamente al iniciar sesión (1 vez por sesión).
  // NO se auto-abre dentro de una sala de clase en vivo: taparía la pregunta
  // interactiva del estudiante e impediría responder.
  useEffect(() => {
    if (!user?.id) return;
    if (/\/clases-en-vivo\/[^/]+\/sala/.test(location.pathname)) return;
    const key = `jasq_tour_autostart_${user.id}`;
    const today = new Date().toISOString().slice(0, 10);  // v5.6.0 · una vez por DÍA (no por pestaña)
    try { if (sessionStorage.getItem(key) === "1" || localStorage.getItem(key) === today) return; } catch (_) {}
    const t = setTimeout(() => {
      try {
        if (sessionStorage.getItem(key) === "1" || localStorage.getItem(key) === today) return;
        sessionStorage.setItem(key, "1");
        localStorage.setItem(key, today);
      } catch (_) {}
      setTourAuto(true);
      setTourOpen(true);
    }, 1400);
    return () => clearTimeout(t);
  }, [user?.id, location.pathname]);

  const greet = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Buenos días";
    if (h < 19) return "Buenas tardes";
    return "Buenas noches";
  })();

  // ----- Búsqueda -----
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null); // null hasta primera búsqueda
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setResults(data);
        setSearchOpen(true);
      } catch {
        setResults({ users: [], templates: [], franchises: [] });
      } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const goToResult = (kind, item) => {
    setSearchOpen(false); setQ("");
    if (kind === "user") {
      // Detalle de usuario adaptado al rol del visor + rol del usuario visualizado.
      if (user?.role === "admin") nav(`/admin/usuario/${item.id}`);
      else if (user?.role === "franquiciado") nav(`/franquiciado/usuario/${item.id}`);
      else if (user?.role === "docente") nav(`/docente/usuario/${item.id}`);
    } else if (kind === "template") {
      if (item.kind === "cad" && (user?.role === "admin")) nav(`/admin/cad-editor/${item.code}`);
      else if (user?.role === "admin") nav(`/admin/cuestionarios?focus=${item.code}`);
      else if (user?.role === "franquiciado") nav(`/franquiciado/resultados-cep?focus=${item.code}`);
      else if (user?.role === "docente") nav(`/docente/cuestionarios?focus=${item.code}`);
    } else if (kind === "franchise") {
      if (user?.role === "admin") nav(`/admin/franquicias?focus=${item.id}`);
      else if (user?.role === "franquiciado") nav(`/franquiciado/franquicia`);
    }
  };

  // ----- Notificaciones -----
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const notifRef = useRef(null);

  // v5.1.0 · sondeo con retroceso: si el servidor está ocupado (520/5xx) NO se insiste cada minuto, y se pausa con la pestaña oculta.
  const loadNotifs = async () => {
    const { data } = await api.get("/notifications");
    setNotifs(data.items || []);
    setUnread(data.unread_count || 0);
  };
  useEffect(() => {
    let stop = false; let timer; let fails = 0;
    const tick = async () => {
      if (stop) return;
      if (!document.hidden) {
        try { await loadNotifs(); fails = 0; } catch { fails += 1; }
      }
      timer = setTimeout(tick, Math.min(300_000, 60_000 * 2 ** Math.min(fails, 3)));
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setNotifs((ns) => ns.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {}
  };
  const onClickNotif = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setNotifs((ns) => ns.map((x) => x.id === n.id ? { ...x, read: true } : x));
        setUnread((u) => Math.max(0, u - 1));
      } catch {}
    }
    if (n.link) nav(n.link);
    setNotifOpen(false);
  };

  // ----- Menú de usuario (avatar dropdown) -----
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [franchiseName, setFranchiseName] = useState("");
  const [profileName, setProfileName] = useState("");
  const userMenuRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Cargar nombre de sede y perfil de permisos del usuario (best-effort).
  useEffect(() => {
    if (!user) return;
    if (user.franchise_id) {
      api.get("/franchises").then(({ data }) => {
        const f = (data || []).find((x) => x.franchise_id === user.franchise_id);
        if (f) setFranchiseName(f.name);
      }).catch(() => {});
    } else {
      setFranchiseName("Sede Principal · matriz");
    }
    if (user.profile_id || user.profile) {
      const inline = user.profile;
      if (inline?.name) { setProfileName(inline.name); return; }
      api.get("/permission-profiles").then(({ data }) => {
        const p = (data || []).find((x) => x.id === user.profile_id);
        if (p) setProfileName(p.name);
      }).catch(() => {});
    } else {
      setProfileName("Por defecto del rol");
    }
  }, [user]);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    nav("/", { replace: true });
  };

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#E1E5F0]" data-testid="app-header">
      <div className="flex items-center justify-between px-4 sm:px-6 md:px-8 lg:px-10 py-3 sm:py-4 gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <Link to={homePath} className="hidden sm:inline-flex shrink-0 mr-1" data-testid="header-logo" title="Inicio">
            <Logo variant="mark" size={22} />
          </Link>
          <button
            type="button"
            onClick={() => nav(homePath)}
            data-testid="header-home-btn"
            aria-label="Ir al Inicio"
            title="Inicio"
            className={`shrink-0 w-10 h-10 grid place-items-center rounded-xl transition-all ${atHome ? "neo-pressed" : "neo-flat hover:-translate-y-0.5"}`}
          >
            <Home size={18} className="text-[#A43790]" />
          </button>
          {!atHome && (
            <button
              type="button"
              onClick={goBack}
              data-testid="header-back-btn"
              aria-label="Volver"
              title="Volver"
              className="shrink-0 w-10 h-10 grid place-items-center rounded-xl neo-flat hover:-translate-y-0.5 transition-all"
            >
              <ArrowLeft size={18} className="text-[#315197]" />
            </button>
          )}
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs tracking-[0.18em] uppercase font-bold text-[#6E7591] truncate">{greet}</p>
            <h2 className="font-display text-base sm:text-xl md:text-2xl font-extrabold text-[#1F2547] truncate">
              {user?.name?.split(" ")[0]}{user?.role === "estudiante" ? ", ¡vamos a aprender!" : ""}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Buscador global */}
          <div ref={searchRef} className="relative hidden md:block">
            <div className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-[#E1E5F0] rounded-xl w-64 focus-within:border-[#A43790]">
              {searching ? <Loader2 size={15} className="text-[#6E7591] animate-spin" /> : <Search size={15} className="text-[#6E7591]" />}
              <input
                data-testid="header-search"
                placeholder="Buscar estudiante, plantilla…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => results && setSearchOpen(true)}
                className="bg-transparent outline-none text-sm flex-1 text-[#1F2547] placeholder:text-[#A6ACC1] font-medium"
              />
              {q && (
                <button onClick={() => { setQ(""); setResults(null); }} className="text-[#A6ACC1] hover:text-[#1F2547]">
                  <X size={14} />
                </button>
              )}
            </div>

            {searchOpen && results && (
              <div className="absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border-2 border-[#E1E5F0] max-h-[60vh] overflow-y-auto z-50" data-testid="search-results">
                {(!results.users?.length && !results.templates?.length && !results.franchises?.length) ? (
                  <p className="px-4 py-6 text-center text-sm text-[#6E7591]">Sin resultados para "{q}"</p>
                ) : (
                  <div className="py-2">
                    {results.users?.length > 0 && (
                      <ResultGroup title="Usuarios" icon={User}>
                        {results.users.map((u) => (
                          <button key={u.id} onClick={() => goToResult("user", u)} data-testid={`search-user-${u.id}`}
                            className="w-full text-left px-4 py-2 hover:bg-[#F7F8FA] flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#E1E5F0] flex items-center justify-center text-xs font-extrabold text-[#1F2547]">
                              {(u.name || "?").slice(0, 1)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-[#1F2547] truncate">{u.name}</p>
                              <p className="text-[11px] text-[#6E7591] truncate">{u.email} · <span className="capitalize">{u.role}</span></p>
                            </div>
                          </button>
                        ))}
                      </ResultGroup>
                    )}
                    {results.templates?.length > 0 && (
                      <ResultGroup title="Cuestionarios" icon={BookOpenCheck}>
                        {results.templates.map((t) => (
                          <button key={t.code} onClick={() => goToResult("template", t)} data-testid={`search-tpl-${t.code}`}
                            className="w-full text-left px-4 py-2 hover:bg-[#F7F8FA] flex items-center gap-3">
                            <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#A43790] bg-[#FFE9F0] px-2 py-0.5 rounded">{t.kind}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-[#1F2547] truncate">{t.title}</p>
                              <p className="text-[11px] text-[#6E7591] truncate font-mono">{t.code}</p>
                            </div>
                          </button>
                        ))}
                      </ResultGroup>
                    )}
                    {results.franchises?.length > 0 && (
                      <ResultGroup title="Franquicias" icon={Briefcase}>
                        {results.franchises.map((f) => (
                          <button key={f.id} onClick={() => goToResult("franchise", f)} data-testid={`search-fra-${f.id}`}
                            className="w-full text-left px-4 py-2 hover:bg-[#F7F8FA] flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-[#1F2547] truncate">{f.name}</p>
                              <p className="text-[11px] text-[#6E7591] truncate">{f.city}, {f.country}</p>
                            </div>
                          </button>
                        ))}
                      </ResultGroup>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tour guiado del Búho (#24) → abre el lanzador de tutoriales */}
          <button
            data-testid="header-tour-btn"
            onClick={() => { setTourAuto(false); setTourOpen(true); }}
            title="Tutoriales guiados de la plataforma"
            className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#E1E5F0] rounded-xl hover:border-[#FFCC28] hover:bg-[#FFF7DA] transition-colors text-lg"
          >
            🦉
          </button>

          {/* Notificaciones */}
          <div ref={notifRef} className="relative">
            <button
              data-testid="header-notifications"
              onClick={() => setNotifOpen((v) => !v)}
              className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#E1E5F0] rounded-xl hover:border-[#F5B82E] transition-colors relative"
            >
              <Bell size={17} className="text-[#4C5478]" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D63384] text-white text-[10px] font-extrabold flex items-center justify-center"
                  data-testid="notif-badge">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border-2 border-[#E1E5F0] z-50 overflow-hidden" data-testid="notif-popover">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#E1E5F0]">
                  <p className="text-sm font-extrabold text-[#1F2547]">Notificaciones</p>
                  {unread > 0 && (
                    <button onClick={markAllRead} data-testid="notif-mark-all"
                      className="text-[11px] font-bold text-[#2C49A0] hover:underline inline-flex items-center gap-1">
                      <CheckCheck size={11} /> Marcar leídas
                    </button>
                  )}
                </div>
                <div className="max-h-[50vh] overflow-y-auto">
                  {notifs.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-[#6E7591]">No tienes notificaciones.</p>
                  ) : notifs.map((n) => (
                    <button key={n.id} onClick={() => onClickNotif(n)} data-testid={`notif-${n.id}`}
                      className={`w-full text-left px-4 py-3 border-b border-[#E1E5F0] last:border-0 hover:bg-[#F7F8FA] ${!n.read ? "bg-[#FFF7DA]/40" : ""}`}>
                      <div className="flex items-start gap-2.5">
                        <div className="relative shrink-0">
                          <img src={OWL_CLASSIC} alt="Zarek" className="w-8 h-8 rounded-full object-cover bg-[#FFF7DA] border border-[#F5B82E]" />
                          {!n.read && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#D63384] border-2 border-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-[#1F2547] leading-snug">{n.title}</p>
                          {n.body && <p className="text-[11px] text-[#6E7591] mt-0.5 line-clamp-2">{n.body}</p>}
                          <p className="text-[10px] text-[#A6ACC1] mt-1 uppercase tracking-wider">
                            {n.created_at ? new Date(n.created_at).toLocaleString("es-ES") : ""}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Avatar / Menú de usuario */}
          <div ref={userMenuRef} className="relative">
            <button
              type="button"
              data-testid="header-user-avatar"
              onClick={() => setUserMenuOpen((v) => !v)}
              className="flex items-center gap-2 pl-1 pr-1 py-1 bg-white border-2 border-[#E1E5F0] rounded-full hover:border-[#F5B82E] transition-colors"
              aria-label="Abrir menú de usuario"
            >
              <img
                src={user?.avatar}
                alt={user?.name}
                className="w-9 h-9 rounded-full object-cover bg-[#F7F8FA] border border-[#E1E5F0]"
              />
            </button>
            {userMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border-2 border-[#E1E5F0] z-50 overflow-hidden"
                data-testid="user-menu-popover"
              >
                <div className="px-4 py-4 bg-gradient-to-br from-[#FFF7DA] to-white border-b border-[#E1E5F0] flex items-center gap-3">
                  <img
                    src={user?.avatar}
                    alt=""
                    className="w-12 h-12 rounded-full bg-white border-2 border-[#F5B82E] object-cover"
                  />
                  <div className="min-w-0">
                    <p className="font-display font-extrabold text-[#1F2547] text-sm truncate" data-testid="user-menu-name">{user?.name}</p>
                    <p className="text-[11px] text-[#6E7591] truncate">{user?.email}</p>
                    <span
                      className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase font-extrabold tracking-wider px-1.5 py-0.5 rounded bg-[#2C49A0]/10 text-[#2C49A0]"
                      data-testid="user-menu-role"
                    >
                      {user?.role}
                    </span>
                  </div>
                </div>
                <div className="px-4 py-3 space-y-2 text-xs">
                  <div className="flex items-start gap-2">
                    <Layers size={14} className="mt-0.5 shrink-0 text-[#A43790]" />
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591]">Perfil</p>
                      <p className="font-bold text-[#1F2547] truncate" data-testid="user-menu-profile">{profileName || "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Building2 size={14} className="mt-0.5 shrink-0 text-[#F5B82E]" />
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591]">Sede</p>
                      <p className="font-bold text-[#1F2547] truncate" data-testid="user-menu-franchise">{franchiseName || "—"}</p>
                    </div>
                  </div>
                </div>
                <div className="px-3 pb-3 space-y-2">
                  {["admin", "docente", "franquiciado"].includes(user?.role) && (
                    <button
                      type="button"
                      onClick={() => { setUserMenuOpen(false); nav(`/${user.role}/mi-perfil`); }}
                      data-testid="user-menu-profile-link"
                      className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-xl bg-[#1F2547] text-white font-bold text-xs hover:bg-[#161A33] transition-colors"
                    >
                      <User size={14} /> Mi perfil
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleLogout}
                    data-testid="user-menu-logout"
                    className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-xl border-2 border-[#FAD0DE] text-[#A52258] font-bold text-xs hover:bg-[#FFE9F0] transition-colors"
                  >
                    <LogOut size={14} /> Cerrar sesión
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <TourLauncher open={tourOpen} autoClose={tourAuto ? 5000 : 0} onClose={() => { setTourOpen(false); setTourAuto(false); }} />
    </header>
  );
}

function ResultGroup({ title, icon: Icon, children }) {
  return (
    <div>
      <p className="px-4 py-1.5 text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] bg-[#F7F8FA] flex items-center gap-1.5">
        <Icon size={11} /> {title}
      </p>
      {children}
    </div>
  );
}
